import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, App, Button, Modal, Segmented } from 'antd';

import ImpactReport from '../tags/ImpactReport';
import { confirmDestructive } from '../common/confirmDestructive';
import { KeptNames, OffChoiceRow, useKeptNames } from '../tags/ItemTagOffParts';
import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import ToggleRow from '../common/ToggleRow';
import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import { getCardItemTagReferences, getCardItemTags, setCardItemTags, setCardWhereReferenced } from '../../services/itemTags';
import { invalidateItemTagCards, invalidateTagOwner } from '../../hooks/useCollectionTags';
import { invalidateItemTagSetting } from '../../hooks/useItemTagSetting';
import { formatCount } from '../../utils/counts';
import { normalizeId } from '../../utils/ids';
import {
	ITEM_TAG_MODES,
	ITEM_TAG_MODE_HINTS,
	ITEM_TAG_MODE_LABELS,
	ITEM_TAG_SETTING_LIMITS,
	cleanupFor,
	cleanupSummary,
	itemTagLockedWords,
	itemTagRefusal,
	itemTagsOn,
	offOutcome,
	passRunning,
	passSummary,
	passWaiting,
	refsPassSummary,
	whereReferencedOffWords,
	whereReferencedRefusal,
} from '../../utils/itemTagSettings';

// ⚑ How often an open window asks again while a pass works (T4d) — and while
// every live pass only waits (the server looks again every minute or ten).
const POLL_MS = 5000;
const WAIT_POLL_MS = 30000;

/**
 * Turning Apply where referenced off, confirmed with the dry run's count
 * (the platform's destructive confirm: red button, Cancel focused). → true
 * to go ahead.
 */
function confirmWhereReferencedOff(modal, automatic) {
	return new Promise(resolve => {
		confirmDestructive(modal, {
			title: 'Turn off Apply where referenced?',
			content: <p>{whereReferencedOffWords(automatic)}</p>,
			okText: 'Turn off',
			onOk: () => resolve(true),
			onCancel: () => resolve(false),
		});
	});
}

const NO_FLOW = Object.freeze({ loading: false, error: null });

/**
 * USE ITEMS AS TAGS — one card's setting in one collection (docs/plans/
 * tags.md §3.2, §5.2 "Collection settings, the cards list", ruled d29, d30,
 * d33, d35–d39, B13, B19; keepr-api § Item tags; build step T4d). Opened from
 * the card's row in Settings › Cards. Manager
 * only; it writes at once through its own endpoint, never with the settings
 * page's Save (the collection PUT refuses to change it).
 *
 * **Off · Chosen items · Every item** (a Segmented — a three-way choice).
 * Picking another one shows what it will do before anything is written:
 *
 * - **Chosen items** — nothing is made; a manager turns each item's tag on
 *   from the item (Use as a tag). From Every item, the tags already made stay.
 * - **Every item** — the card's items here are COUNTED first (a dry run):
 *   the count, a warning over 1,000, a refusal over 10,000 (ruled d35). The
 *   real call confirms the warning; the backfill is a paged pass.
 * - **Off** — the dry run's totals, then the off dialog for every one of the
 *   card's item tags (d30): what happens to applications by a rule and to
 *   those put on by hand, Remove or Keep each.
 *   - Keeping either converts each tag into a collection tag named after its
 *     item, which everyone who reads the collection's tags then sees — so
 *     the exact names are listed first (a dry run with the choices) and the
 *     real call echoes their digest, `confirmNames` (owner call B19). Names
 *     that changed since answer 409 `confirm_names_required`: they are listed
 *     again. An item the manager cannot read refuses it (B13, `private_item_tags`).
 *   - Removing both deletes them, so the card's IMPACT REPORT comes first,
 *     with Remove for me / Leave it per entry (d33, d37).
 *
 * The pass a switch starts (Every item's backfill, Off's turning-off) is
 * shown while the window is open, asked for again every few seconds while
 * it works (every 30 s while it only waits on an archived collection).
 *
 * **Apply where referenced** (d29, on by default; keepr-api T6b) switches ON
 * ITS OWN — `{ applyWhereReferenced }` with no mode, so nothing is
 * re-counted and no backfill restarts — and only on the card's own setting
 * (an inherited one is switched where it comes from). Turning it off asks a
 * dry run first and confirms its count ("Takes the tag off N items where it
 * was applied because they refer to the item"); ⚑ with nothing to take off
 * it switches at once. Either way the switch starts the where-referenced
 * pass (`refsPass`), shown under the switch with how far it has got and why
 * it waits (`item_tags_backfill`, `collection_archived`).
 *
 * The GET's `canManage` says whether this person may change any of it here
 * (manage on the collection, and the collection not archived — what the
 * endpoint itself asks). When it is false the setting is shown as it stands,
 * the choices and the switch are disabled, and one line says why
 * (`archived`, what the Card Library knows of the collection, picks the
 * words). The two questions, their counts and the names list are the off
 * dialog's shared pieces (tags/ItemTagOffParts.js), which one item's Stop
 * using as a tag uses too.
 *
 * `onChanged(result)` hears every write that landed (the settings form
 * re-reads the collection: the endpoint moved its `cards[]`).
 */
export default function ItemTagModeModal({
	open,
	onClose,
	collectionId,
	collectionName = null,
	card,
	cardsById = null,
	archived = false,
	onChanged,
}) {
	const { message, modal } = App.useApp();
	const cid = normalizeId(collectionId);
	const cardId = normalizeId(card?.id || card?._id);
	const cardName = card?.name || 'This card';
	const [status, setStatus] = useState({ loading: true, error: null, value: null });
	const [draft, setDraft] = useState(null);
	const [flow, setFlow] = useState(NO_FLOW);
	const [busy, setBusy] = useState(false);
	const [refusal, setRefusal] = useState(null);
	const [toggling, setToggling] = useState(false);
	const liveRef = useRef(true);
	const kept = useKeptNames(body => setCardItemTags(cid, cardId, body));
	const clearNames = kept.clear;

	const current = status.value?.mode || null;
	const changing = Boolean(draft) && Boolean(current) && draft !== current;
	// The API's word on whether this person may change it here (never `pass`).
	const locked = Boolean(status.value) && status.value.canManage !== true;

	const loadStatus = useCallback(async ({ quiet = false } = {}) => {
		if (!cid || !cardId) return;
		if (!quiet) setStatus(prev => ({ ...prev, loading: true, error: null }));
		try {
			const value = await getCardItemTags(cid, cardId);
			if (!liveRef.current) return;
			setStatus({ loading: false, error: null, value });
			setDraft(prev => (prev && quiet ? prev : value.mode));
		} catch (error) {
			if (!liveRef.current) return;
			setStatus(prev => ({ loading: false, error: quiet ? prev.error : error, value: quiet ? prev.value : null }));
		}
	}, [cid, cardId]);

	useEffect(() => {
		liveRef.current = true;
		return () => { liveRef.current = false; };
	}, []);

	useEffect(() => {
		if (!open) return;
		setDraft(null);
		setFlow(NO_FLOW);
		clearNames();
		setRefusal(null);
		setBusy(false);
		loadStatus();
	}, [open, loadStatus, clearNames]);

	// While a pass works — the mode's or the where-referenced one — the open
	// window asks again; more slowly while every live one only waits.
	const pass = status.value?.pass || null;
	const refsPass = status.value?.refsPass || null;
	const running = passRunning(pass) || passRunning(refsPass);
	const onlyWaiting = running
		&& (!passRunning(pass) || passWaiting(pass))
		&& (!passRunning(refsPass) || passWaiting(refsPass));
	const pollMs = onlyWaiting ? WAIT_POLL_MS : POLL_MS;
	useEffect(() => {
		if (!open || !running) return undefined;
		const timer = setInterval(() => loadStatus({ quiet: true }), pollMs);
		return () => clearInterval(timer);
	}, [open, running, pollMs, loadStatus]);

	// ----------------------------------------------------------- the dry runs

	const startFlow = useCallback(async (mode) => {
		setRefusal(null);
		clearNames();
		if (mode === 'chosen') { setFlow({ ...NO_FLOW, ready: true }); return; }
		setFlow({ loading: true, error: null });
		try {
			const answer = await setCardItemTags(cid, cardId, { mode, dryRun: true });
			if (!liveRef.current) return;
			if (mode === 'every') {
				setFlow({ loading: false, error: null, count: Number(answer.count) || 0, warn: Boolean(answer.warn), refused: Boolean(answer.refused) });
			} else {
				setFlow({ loading: false, error: null, totals: answer.totals || {}, automatic: null, hand: null, report: null, choices: {} });
			}
		} catch (error) {
			if (!liveRef.current) return;
			const data = error?.response?.data || {};
			if (mode === 'every' && data.code === 'too_many_items') {
				setFlow({ loading: false, error: null, count: Number(data.count) || 0, warn: true, refused: true });
				return;
			}
			setFlow({ loading: false, error: itemTagRefusal(error, 'The card’s items could not be counted. Please try again.') });
		}
	}, [cid, cardId, clearNames]);

	const choose = (mode) => {
		if (locked) return;
		setDraft(mode);
		if (mode === current) { setFlow(NO_FLOW); clearNames(); setRefusal(null); return; }
		startFlow(mode);
	};

	// Off, with tags to turn off: the names a keep publishes (B19), or the
	// report a removal needs (d33) — asked once the two choices are made.
	const outcome = draft === 'off' && flow.totals ? offOutcome({ automatic: flow.automatic, hand: flow.hand }) : null;
	const tagCount = Number(flow.totals?.tags) || 0;

	const loadKeptNames = kept.load;
	const loadNames = useCallback(async ({ automatic, hand, again = false }) => {
		const answer = await loadKeptNames({ mode: 'off', dryRun: true, automatic, hand }, { again });
		if (answer?.totals && liveRef.current) setFlow(prev => ({ ...prev, totals: answer.totals }));
	}, [loadKeptNames]);

	const loadReport = useCallback(async () => {
		setFlow(prev => ({ ...prev, report: { loading: true, loaded: false, error: null, references: [], truncated: false } }));
		try {
			const answer = await getCardItemTagReferences(cid, cardId);
			if (!liveRef.current) return;
			setFlow(prev => ({ ...prev, report: { loading: false, loaded: true, error: null, ...answer } }));
		} catch (error) {
			if (!liveRef.current) return;
			setFlow(prev => ({ ...prev, report: { loading: false, loaded: true, error, references: [], truncated: false } }));
		}
	}, [cid, cardId]);

	const setOffChoice = (field, value) => {
		setRefusal(null);
		const next = { automatic: flow.automatic, hand: flow.hand, [field]: value };
		const out = offOutcome(next);
		setFlow(prev => ({ ...prev, [field]: value }));
		clearNames();
		if (out === 'convert') loadNames(next);
		if (out === 'delete' && !flow.report) loadReport();
	};

	// ----------------------------------------------------------- the commit

	const finished = (result, words) => {
		invalidateItemTagSetting(cid);
		invalidateItemTagCards(cid);
		const summary = passSummary(result?.pass);
		const { done, failed } = cleanupSummary(result?.cleanup);
		message.success([words, summary && summary.tone !== 'success' ? summary.text : null, done].filter(Boolean).join(' '));
		if (failed.length) message.warning(`${failed.length === 1 ? 'One reference' : `${failed.length} references`} could not be removed for you, and stop${failed.length === 1 ? 's' : ''} matching.`);
		onChanged?.(result);
		onClose?.();
	};

	const commit = async () => {
		if (!changing || busy) return;
		setBusy(true);
		setRefusal(null);
		try {
			if (draft === 'chosen') {
				const result = await setCardItemTags(cid, cardId, { mode: 'chosen' });
				finished(result, `Items of ${cardName} can now be used as tags, one by one: Use as a tag, on each item.`);
			} else if (draft === 'every') {
				const result = await setCardItemTags(cid, cardId, { mode: 'every', ...(flow.warn ? { confirm: true } : {}) });
				finished(result, `Every item of ${cardName} gets a tag.`);
			} else if (draft === 'off') {
				let body = { mode: 'off' };
				if (tagCount > 0 && outcome === 'convert') body = { mode: 'off', automatic: flow.automatic, hand: flow.hand, confirmNames: kept.names?.digest || undefined };
				if (tagCount > 0 && outcome === 'delete') body = { mode: 'off', automatic: 'remove', hand: 'remove', cleanup: cleanupFor(flow.report?.references, flow.choices) };
				if (body.cleanup && !body.cleanup.length) delete body.cleanup;
				const result = await setCardItemTags(cid, cardId, body);
				if (outcome === 'convert') invalidateTagOwner(cid);
				finished(result, `Items of ${cardName} are no longer used as tags.`);
			}
		} catch (error) {
			const data = error?.response?.data || {};
			if (data.code === 'confirm_names_required' && draft === 'off') {
				// The names moved since they were listed (B19): list them again.
				loadNames({ automatic: flow.automatic, hand: flow.hand, again: true });
			} else if (data.code === 'confirm_required' && draft === 'every') {
				setFlow(prev => ({ ...prev, count: Number(data.count) || prev.count, warn: true }));
			} else if (data.code === 'too_many_items' && draft === 'every') {
				setFlow(prev => ({ ...prev, count: Number(data.count) || prev.count, warn: true, refused: true }));
			}
			setRefusal(itemTagRefusal(error, 'The setting could not be changed. Please try again.'));
		} finally {
			setBusy(false);
		}
	};

	// Apply where referenced — this card's own setting only, switched on its
	// own (keepr-api T6b: no mode, so no re-count and no backfill restart).
	const own = normalizeId(status.value?.fromCardId) === cardId;
	const toggleWhereReferenced = async (value) => {
		if (locked || !current || !itemTagsOn(current) || !own || toggling) return;
		const want = value === true;
		setToggling(true);
		setRefusal(null);
		try {
			if (!want) {
				// Off takes the where-referenced applications off: say how many first.
				const dry = await setCardWhereReferenced(cid, cardId, false, { dryRun: true });
				if (!liveRef.current) return;
				const automatic = Number(dry?.automatic) || 0;
				if (automatic > 0 && !(await confirmWhereReferencedOff(modal, automatic))) return;
			}
			const result = await setCardWhereReferenced(cid, cardId, want);
			if (!liveRef.current) return;
			setStatus(prev => (prev.value ? {
				...prev,
				value: {
					...prev.value,
					applyWhereReferenced: typeof result?.applyWhereReferenced === 'boolean' ? result.applyWhereReferenced : want,
					refsPass: result?.refsPass !== undefined ? result.refsPass : prev.value.refsPass,
				},
			} : prev));
			invalidateItemTagSetting(cid);
			invalidateItemTagCards(cid);
			await loadStatus({ quiet: true });
			onChanged?.(null);
		} catch (error) {
			if (!liveRef.current) return;
			setRefusal(whereReferencedRefusal(error, { cardName }));
			// Inherited or Off since the window read it: show where it stands now.
			if (error?.response?.data?.code === 'item_tags_off') loadStatus({ quiet: true });
		} finally {
			if (liveRef.current) setToggling(false);
		}
	};

	// ----------------------------------------------------------- the words

	const primary = (() => {
		if (!changing) return null;
		if (draft === 'chosen') return { label: 'Use chosen items', disabled: false };
		if (draft === 'every') return { label: 'Use every item', disabled: flow.loading || Boolean(flow.error) || flow.refused || flow.count === undefined };
		if (draft === 'off') {
			if (flow.loading || flow.error || !flow.totals) return { label: 'Turn off', disabled: true };
			if (tagCount === 0) return { label: 'Turn off', disabled: false };
			if (outcome === 'convert') return { label: 'Keep as collection tags', disabled: !kept.names || kept.names.loading || Boolean(kept.names.error) || !kept.names.digest };
			if (outcome === 'delete') return { label: 'Turn off and remove tags', disabled: !flow.report?.loaded, danger: true };
			return { label: 'Turn off', disabled: true };
		}
		return null;
	})();

	const summary = passSummary(pass);
	const refsSummary = refsPassSummary(refsPass, { applying: status.value?.applyWhereReferenced !== false });
	const inheritedFrom = status.value?.fromCardId && !own ? status.value.fromCardId : null;
	const inheritedName = inheritedFrom
		? ((cardsById instanceof Map ? cardsById.get(inheritedFrom) : cardsById?.[inheritedFrom])?.name || 'the card it extends')
		: null;

	const offChoiceRow = (field, label, count, testId) => (
		<OffChoiceRow
			label={label}
			count={count}
			value={flow[field]}
			disabled={busy}
			onChange={value => setOffChoice(field, value)}
			testId={testId}
		/>
	);

	const flowPanel = (() => {
		if (!changing) return null;
		if (flow.loading || flow.error) {
			return (
				<DialogLoadState
					loaded={false}
					loading={flow.loading}
					error={Boolean(flow.error)}
					errorDetail={flow.error || undefined}
					label="the count"
					onRetry={() => startFlow(draft)}
					skeleton={<Skeleton variant="line" count={3} />}
					minHeight={96}
				/>
			);
		}
		if (draft === 'chosen') {
			return (
				<p className="item-tag-mode__step" data-testid="item-tag-mode-chosen">
					{current === 'every'
						? 'The tags already made stay. From now on, a manager turns each new item’s tag on from the item.'
						: 'Nothing changes on any item yet. A manager turns an item’s tag on from the item: Use as a tag.'}
				</p>
			);
		}
		if (draft === 'every') {
			const n = flow.count || 0;
			if (flow.refused) {
				return (
					<Alert
						type="error"
						showIcon
						data-testid="item-tag-mode-every"
						message={`This card has ${n.toLocaleString('en-US')} items here.`}
						description={`Every item works for up to ${ITEM_TAG_SETTING_LIMITS.refuseAbove.toLocaleString('en-US')} items. Use Chosen items instead, and turn on the tags you need one by one.`}
					/>
				);
			}
			if (!n) return <p className="item-tag-mode__step" data-testid="item-tag-mode-every">This card has no items here yet. Each new one will get a tag.</p>;
			if (flow.warn) {
				return (
					<Alert
						type="warning"
						showIcon
						data-testid="item-tag-mode-every"
						message={`Each of the card’s ${n.toLocaleString('en-US')} items here gets a tag.`}
						description={`That is more than ${ITEM_TAG_SETTING_LIMITS.warnAt.toLocaleString('en-US')} tags, and each new item gets one too. They are made a batch at a time; you can close this while it works.`}
					/>
				);
			}
			return (
				<p className="item-tag-mode__step" data-testid="item-tag-mode-every">
					{`Each of the card’s ${formatCount(n, 'item')} here gets a tag, and so does each new one.`}
				</p>
			);
		}
		if (draft === 'off') {
			const totals = flow.totals || {};
			const deleted = Number(totals.deletedItemTags) || 0;
			const deletedNote = deleted
				? `${formatCount(deleted, 'deleted item')} ${deleted === 1 ? 'was' : 'were'} used as ${deleted === 1 ? 'a tag' : 'tags'}; restored, ${deleted === 1 ? 'it comes' : 'they come'} back without one.`
				: null;
			if (!tagCount) {
				return (
					<div className="item-tag-mode__step" data-testid="item-tag-mode-off">
						<p>No item of this card is used as a tag here.</p>
						{deletedNote ? <p className="meta-line">{deletedNote}</p> : null}
					</div>
				);
			}
			return (
				<div className="item-tag-mode__step" data-testid="item-tag-mode-off">
					<p>
						{`${formatCount(tagCount, 'item')} of this card ${tagCount === 1 ? 'is' : 'are'} used as ${tagCount === 1 ? 'a tag' : 'tags'}, on ${formatCount(Number(totals.items) || 0, 'item') || 'no items'}. Choose what happens where they are on. Keeping either turns each into an ordinary collection tag; removing both deletes them.`}
					</p>
					{offChoiceRow('automatic', 'Applied by a rule', Number(totals.automatic) || 0, 'off-choice-automatic')}
					{offChoiceRow('hand', 'Put on by hand', Number(totals.hand) || 0, 'off-choice-hand')}
					{deletedNote ? <p className="meta-line">{deletedNote}</p> : null}
					{outcome === 'convert' ? (
						<KeptNames names={kept.names} className="item-tag-mode__names-panel" testId="item-tag-mode-names">
							{list => (
								<>
									<p>
										{`These ${formatCount(list.length, 'collection tag') || 'collection tags'} will be made. Everyone who can see this collection’s tags will see their names, even people who cannot read the items:`}
									</p>
									<ol className="item-tag-mode__names" aria-label="The names the tags will get">
										{list.map(entry => <li key={entry.itemId}>{entry.name}</li>)}
									</ol>
									<p className="meta-line">A name another tag here already has gets a number after it, like “Mom (2)”. Anyone’s private uses of these tags are removed.</p>
								</>
							)}
						</KeptNames>
					) : null}
					{outcome === 'delete' ? (
						<div className="item-tag-mode__report" data-testid="item-tag-mode-report">
							<p>Removing both deletes the tags: they come off every item. Using the items as tags again later makes new tags, so what names these stops matching.</p>
							<ImpactReport
								references={flow.report?.references || []}
								truncated={flow.report?.truncated}
								loading={!flow.report || flow.report.loading}
								error={flow.report?.error}
								choices={flow.choices || {}}
								onChange={(key, value) => setFlow(prev => ({ ...prev, choices: { ...(prev.choices || {}), [key]: value } }))}
								disabled={busy}
								subject="these tags"
								onRetry={loadReport}
							/>
						</div>
					) : null}
				</div>
			);
		}
		return null;
	})();

	const footer = [
		<Button key="close" onClick={onClose} disabled={busy}>{changing ? 'Cancel' : 'Close'}</Button>,
		primary ? (
			<Button
				key="primary"
				type="primary"
				danger={Boolean(primary.danger)}
				loading={busy}
				disabled={primary.disabled}
				onClick={commit}
			>
				{primary.label}
			</Button>
		) : null,
	].filter(Boolean);

	return (
		<Modal
			open={open}
			title="Use items as tags"
			width={600}
			onCancel={onClose}
			footer={footer}
			destroyOnHidden
			className="item-tag-mode"
		>
			<div className="modal-identity">
				<RecordIdentity kicker="Card" badge={<RecordBadge kind="card" record={card?.def || { name: cardName }} title={cardName} size="sm" />} />
			</div>
			<p className="item-tag-mode__lead">
				{`Let the items of this card be put on other items as tags${collectionName ? `, here in ${collectionName}` : ' in this collection'}.`}
			</p>
			<DialogLoadState
				loaded={Boolean(status.value)}
				loading={status.loading && !status.value}
				error={Boolean(status.error) && !status.value}
				errorDetail={status.error ? itemTagRefusal(status.error, 'This card’s setting could not be read.') : undefined}
				label="this card’s setting"
				onRetry={() => loadStatus()}
				skeleton={<Skeleton variant="line" count={3} />}
				minHeight={160}
			>
				{status.value ? (
				<>
					<Segmented
						block
						className="item-tag-mode__modes"
						aria-label="Use items as tags"
						value={draft || current}
						disabled={busy || locked}
						onChange={choose}
						options={ITEM_TAG_MODES.map(mode => ({ value: mode, label: ITEM_TAG_MODE_LABELS[mode] }))}
					/>
					<p className="meta-line item-tag-mode__hint">{ITEM_TAG_MODE_HINTS[draft || current]}</p>
					{locked ? (
						<Alert
							type="info"
							showIcon
							className="item-tag-mode__locked"
							data-testid="item-tag-mode-locked"
							message={itemTagLockedWords({ archived })}
						/>
					) : null}
					{inheritedFrom ? (
						<Alert
							type="info"
							showIcon
							className="item-tag-mode__inherited"
							message={`Set on ${inheritedName}, for this card and the cards that extend it.`}
							description={`Choosing here gives ${cardName} a setting of its own.`}
						/>
					) : null}
					{!changing ? (
						<>
							{status.value.count ? (
								<p className="item-tag-mode__count" data-testid="item-tag-mode-count">
									{`${formatCount(status.value.count, 'item')} of this card ${status.value.count === 1 ? 'is' : 'are'} used as ${status.value.count === 1 ? 'a tag' : 'tags'}.`}
								</p>
							) : null}
							{summary ? (
								<Alert
									type={summary.tone}
									showIcon
									className="item-tag-mode__pass"
									data-testid="item-tag-mode-pass"
									message={summary.text}
									description={summary.notes.length ? summary.notes.join(' ') : undefined}
								/>
							) : null}
							{itemTagsOn(current) && own ? (
								<ToggleRow
									first
									id="item-tag-mode-where-referenced"
									label="Apply where referenced"
									description="Put an item’s tag on the items that refer to it through an item lookup, in this collection’s family. Tags put on by hand stay."
									checked={status.value.applyWhereReferenced !== false}
									disabled={toggling || locked}
									onChange={toggleWhereReferenced}
								/>
							) : null}
							{itemTagsOn(current) && own && refsSummary ? (
								<Alert
									type={refsSummary.tone}
									showIcon
									className="item-tag-mode__refs-pass"
									data-testid="item-tag-mode-refs-pass"
									message={refsSummary.text}
									description={refsSummary.notes.length ? refsSummary.notes.join(' ') : undefined}
								/>
							) : null}
						</>
					) : flowPanel}
				</>
				) : null}
			</DialogLoadState>
			{refusal ? <Alert type="error" showIcon className="item-tag-mode__refusal" message={refusal} data-testid="item-tag-mode-refusal" /> : null}
		</Modal>
	);
}
